import { useCallback } from 'react';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import { useParams } from 'react-router-dom';
import useDedicatedFileUpload from './useDedicatedFileUpload';
import useCaseRecordDelete from './useCaseRecordDelete';
import useMedicalButton from './useMedicalButton';
import { buildMedicalProviderPaths } from '../utils/caseFieldPaths';
import { prepFileDataForDedicatedUpload, validationFilesFunc, filterDuplicateFilesByName } from '../utils/helper';
import { RECORD_DATA_TYPE } from '@demandproai/shared';

const useMedicalProvider = ({ providerIndex, providerType }) => {
    const { formik, selectedTiffIndex, setFileErrorList, toggleReorderSection } = useAddCaseFormContext();
    const { caseId } = useParams();
    const { handleFileRemove } = useCaseRecordDelete();

    const formikValues = formik.values;
    const setFieldValue = formik.setFieldValue;

    const { dedicatedFileUpload, isLoading: fileUploadLoading, completeFileUpload, startFileUpload } = useDedicatedFileUpload(caseId, RECORD_DATA_TYPE.MEDICAL);

    const medicalproviderPaths = buildMedicalProviderPaths({ selectedTiffIndex, providerType });
    const medicalRecords = formikValues?.clients?.[selectedTiffIndex]?.medicalProviders?.[providerType]?.medicalRecords?.[providerIndex]?.source || [];

    const getIsDuplicateProviderName = useCallback((e, index) => {
        const filteredValue = formikValues.clients[selectedTiffIndex].medicalProviders?.[providerType]?.medicalRecords?.find((obj, objIndex) => {
            if (objIndex === index) { return false }
            return obj.providerName.trim().toLowerCase() === e.target.value.trim().toLowerCase()
        });
        return Boolean(filteredValue);
    }, [formikValues, selectedTiffIndex, providerType]);

    const { isButonDisabled, buttonDisplayText } = useMedicalButton(true, getIsDuplicateProviderName);

    const deleteProvider = async () => {
        const providerToDelete = formikValues?.clients[selectedTiffIndex]?.medicalProviders?.[providerType]?.medicalRecords?.[providerIndex];
        handleFileRemove({
            formik,
            providerIndex: providerIndex,
            fileToRemove: providerToDelete.source,
            filePath: `clients[${selectedTiffIndex}].medicalProviders.${providerType}.medicalRecords`,
            removeData: { [medicalproviderPaths.records]: providerToDelete },
            skipFileKeyValidation: true
        })
    };

    const removeMedicalFile = (fileToRemove) => {
        handleFileRemove({
            formik,
            fileToRemove: fileToRemove,
            filePath: `clients[${selectedTiffIndex}].medicalProviders.${providerType}.medicalRecords[${providerIndex}].source`,
            removeData: { [medicalproviderPaths.source(providerIndex)]: { s3UrlPath: fileToRemove.s3UrlPath } }
        })
    };

    const onFileUpload = async (files) => {
        const newFiles = Array.from(files.file);
        const recordPath = `clients[${selectedTiffIndex}].medicalProviders.${providerType}.medicalRecords[${providerIndex}].source`;
        const existingRecords = formikValues?.clients?.[selectedTiffIndex]?.medicalProviders?.[providerType]?.medicalRecords?.[providerIndex]?.source || [];
        const uniqueNewFiles = filterDuplicateFilesByName({
            newFiles,
            prevFiles:existingRecords,
        });
        if (!uniqueNewFiles.length) return;
        const validFiles = await validationFilesFunc(uniqueNewFiles, setFileErrorList);

        const combinedArr = [...existingRecords, ...validFiles];
        const updatedRecords = [...new Map(combinedArr.map(item => [item.name, item])).values()];
        setFieldValue(recordPath, updatedRecords);
        const prepFiles = prepFileDataForDedicatedUpload(validFiles)
        const fileCount = prepFiles.length;

        startFileUpload(fileCount);
        const dedicatedFiles = await dedicatedFileUpload(prepFiles, {
            fieldKey: medicalproviderPaths.source(providerIndex),
            action: 'add'
        });
        const newCombinedArr = [...existingRecords, ...dedicatedFiles];
        const newUpdatedRecords = [...new Map(newCombinedArr.map(item => [item.name, item])).values()];
        setFieldValue(recordPath, newUpdatedRecords);

        completeFileUpload(fileCount);
    };

    const handleOpenReorder = () => {
        toggleReorderSection(providerType);
    };

    return {
        medicalRecords,
        fileUploadLoading,
        isButonDisabled,
        buttonDisplayText,
        deleteProvider,
        removeMedicalFile,
        onFileUpload,
        handleOpenReorder
    };
};

export default useMedicalProvider;
